import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NavigationEnd, Router, RouterModule } from "@angular/router";
import { filter } from "rxjs";
import { SnackerService } from 'src/app/shared/snacker.service';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { Menu } from "src/models/others/Menu";
import { MenuHeader } from "src/models/others/MenuHeader";
import { Mode } from "src/models/others/Mode";
import { StorageEnum } from "src/models/others/StorageEnum";
import { DatatransferService } from "src/services/datatransfer.service";
import { ModeService } from "src/services/mode.service";

@Component({
  selector: "app-side-nav",
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, MatSnackBarModule],
  templateUrl: "./side-nav.component.html",
  styleUrls: ["./side-nav.component.css"],
})
export class SideNavComponent implements OnInit {
  menuList: MenuHeader[] = [];
  mode: string;
  changeColor: boolean = false;
  activeItem: any;

  @Input() sideNavStatus: boolean = true;
  @Output() sideNavOpenRequested = new EventEmitter<void>();
  @Output() sideNavCloseRequested = new EventEmitter<void>();
  constructor(
    private modeSwitch: ModeService,
    private dataTransfer: DatatransferService,
    private snacker: SnackerService,
    private router: Router,
  ) { }

  ngOnInit() {
    try {
      const storedMenuHeaders = sessionStorage.getItem(StorageEnum.getMenuHeaderList);
      this.menuList = storedMenuHeaders ? JSON.parse(storedMenuHeaders) : (this.dataTransfer.getMenuList() || []);

      if (!this.menuList?.length) {
        const idList = this.parseStorageList(sessionStorage.getItem(StorageEnum.getMenuIdList));
        const nameList = this.parseStorageList(sessionStorage.getItem(StorageEnum.getMenuNameList));
        if (idList.length && idList.length === nameList.length) {
          const flatMenu: Menu[] = idList.map((id: any, index: number) => ({
            menuId: Number(id),
            menuName: String(nameList[index])
          } as Menu));
          this.menuList = [{ menuHeaderId: 0, menuHeaderName: 'Menu', subMenuList: flatMenu }];
        }
      }

      this.dataTransfer.setMenuList(this.menuList);
      const selectedMenu = this.getSelectedMenuForCurrentState();
      if (selectedMenu) {
        this.setActiveMenu(selectedMenu);
        this.openActiveMenuDrawer();
        // this.routeMenu(firstMenu.menuId);
      }

      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(() => {
          const routeMenu = this.getMenuByCurrentRoute();

          if (routeMenu) {
            this.setActiveMenu(routeMenu);
            this.openActiveMenuDrawer();
          }
        });
    } catch (e) {
      this.snacker.error("Server Error");
    }
  }

  private parseStorageList(value: string | null): any[] {
    if (!value) {
      return [];
    }
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
    }
  }

  onMenuClick(menu: Menu) {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      this.sideNavCloseRequested.emit();
    } else {
      this.sideNavOpenRequested.emit();
    }
    this.setActiveMenu(menu);
    this.openMenuDrawerByMenuId(menu.menuId);
    this.routeMenu(menu.menuId);
  }

  openedMenuId: number | null = null;
  openedMenuIds: number[] = [];

  toggleMenu(menuHeaderId: number) {
    this.sideNavOpenRequested.emit();

    if (this.openedMenuIds.includes(menuHeaderId)) {

      this.openedMenuIds =
        this.openedMenuIds.filter(
          id => id !== menuHeaderId
        );

    } else {

      this.openedMenuIds.push(menuHeaderId);
    }
  }

  private getSelectedMenuForCurrentState(): Menu | null {
    const routeMenu = this.getMenuByCurrentRoute();

    if (routeMenu) {
      return routeMenu;
    }

    const storedMenuId = sessionStorage.getItem(StorageEnum.getSelectedMenuId);
    const storedMenu = storedMenuId ? this.getMenuById(Number(storedMenuId)) : null;

    if (storedMenu) {
      return storedMenu;
    }

    const transferMenuName = this.dataTransfer.getMenuName();
    const transferMenu = transferMenuName ? this.getMenuByName(transferMenuName) : null;

    return transferMenu || this.menuList[0]?.subMenuList?.[0] || null;
  }

  private getMenuByCurrentRoute(): Menu | null {
    const menuId = this.getMenuIdFromRoute(this.router.url);
    return menuId ? this.getMenuById(menuId) : null;
  }

  private getMenuIdFromRoute(url: string): number | null {
    const routePath = url.split('?')[0].split('#')[0];

    const routeMenuMap: Record<string, number> = {
      '/homepage/my-task-list': 1,
      '/homepage/assign-task-list': 2,
      '/homepage/review-task-list': 3,
      '/homepage/person-management': 4,
      '/homepage/task-report': 5,
      '/homepage/leave-report': 6,
      '/homepage/team-lead': 7,
      '/homepage/projects': 8,
      '/homepage/employee-timeline': 9,
      '/homepage/workingdays-report': 10,
      '/homepage/project-report': 11,
      '/homepage/leadproject-report': 12,
      '/homepage/allocation-report': 13
    };

    return routeMenuMap[routePath] || null;
  }

  private getMenuById(menuId: number): Menu | null {
    for (const header of this.menuList) {
      const menu = header.subMenuList?.find((submenu) => Number(submenu.menuId) === Number(menuId));

      if (menu) {
        return menu;
      }
    }

    return null;
  }

  private getMenuByName(menuName: string): Menu | null {
    for (const header of this.menuList) {
      const menu = header.subMenuList?.find((submenu) => submenu.menuName === menuName);

      if (menu) {
        return menu;
      }
    }

    return null;
  }

  private setActiveMenu(menu: Menu): void {
    this.dataTransfer.setMenuName(menu.menuName);
    this.dataTransfer.setMenuId(menu.menuId);
    this.activeItem = menu.menuName;
    sessionStorage.setItem(StorageEnum.getSelectedMenuId, String(menu.menuId));
    sessionStorage.setItem(StorageEnum.getSelectedMenuName, menu.menuName);
  }

  private openActiveMenuDrawer(): void {
    const activeHeader = this.menuList.find((header) =>
      header.subMenuList?.some((submenu) => submenu.menuName === this.activeItem)
    );

    if (activeHeader) {
      this.openMenuDrawer(activeHeader.menuHeaderId);
    }
  }

  private openMenuDrawerByMenuId(menuId: number): void {
    const activeHeader = this.menuList.find((header) =>
      header.subMenuList?.some((submenu) => Number(submenu.menuId) === Number(menuId))
    );

    if (activeHeader) {
      this.openMenuDrawer(activeHeader.menuHeaderId);
    }
  }

  private openMenuDrawer(menuHeaderId: number): void {
    if (!this.openedMenuIds.includes(menuHeaderId)) {
      this.openedMenuIds.push(menuHeaderId);
    }
  }

  isParentActive(header: any): boolean {

    return header.subMenuList?.some(
      (submenu: any) =>
        submenu.menuName === this.activeItem
    );
  }

  getParentIcon(headerName: string): string {
    const name = headerName.toLowerCase().trim();

    if (name.includes('task')) {
      return 'fa-list-check';     // Crisp task checkboxes icon
    } else if (name.includes('manage') || name.includes('admin')) {
      return 'fa-sliders';        // Modern dashboard controls configuration sliders
    } else if (name.includes('report') || name.includes('chart')) {
      return 'fa-chart-pie';      // Sleek analytics pie chart data icon
    }

    // Clean fallback if a new parent menu is added later
    return 'fa-folder-closed';
  }

  private routeMenu(menuId: number) {
    switch (Number(menuId)) {
      case 1: {
        this.modeSwitch.setMode(Mode.taskMode);
        this.router.navigate(["homepage/my-task-list"]);
        break;
      }
      case 2: {
        this.modeSwitch.setMode(Mode.assignMode);
        this.router.navigate(["homepage/assign-task-list"]);
        break;
      }
      case 3: {
        this.modeSwitch.setMode(Mode.reviewMode);
        this.router.navigate(["homepage/review-task-list"]);
        break;
      }
      case 4: {
        this.modeSwitch.setMode(Mode.reviewMode);
        this.router.navigate(["homepage/person-management"]);
        break;
      }
      case 5: {
        this.modeSwitch.setMode(Mode.reviewMode);
        this.router.navigate(["homepage/task-report"]);
        break;
      }
      case 6: {
        this.modeSwitch.setMode(Mode.reviewMode);
        this.router.navigate(["homepage/leave-report"]);
        break;
      }
      case 7: {
        this.modeSwitch.setMode(Mode.reviewMode);
        this.router.navigate(["homepage/team-lead"]);
        break;
      }
      case 8: {
        this.modeSwitch.setMode(Mode.reviewMode);
        this.router.navigate(["homepage/projects"]);
        break;
      }
      case 9: {
        this.router.navigate(["homepage/employee-timeline"]);
        break;
      }
      case 10: {
        this.router.navigate(["homepage/workingdays-report"]);
        break;
      }
      case 11: {
        this.router.navigate(["homepage/project-report"]);
        break;
      }
      case 12: {
        this.router.navigate(["homepage/leadproject-report"]);
        break;
      }
      case 13: {
        this.router.navigate(["homepage/allocation-report"]);
        break;
      }
      default: {
        return null;
      }
    }
  }

  getMenuIcon(menuId: number) {
    switch (Number(menuId)) {
      case 1:
        return "fa-solid fa-list-check";
      case 2:
        return "fa-solid fa-bars-progress";
      case 3:
        return "fa-solid fa-star";
      case 4:
        return "fa-solid fa-user";
      case 5:
        return "fa-solid fa-file";
      case 6:
        return "fa-solid fa-arrow-right-from-bracket";
      case 7:
        return "fa-solid fa-people-group";
      case 8:
        return "fa-solid fa-file-powerpoint";
      case 9:
        return "fa-solid fa-timeline";
      case 10:
      case 11:
        return "fa-solid fa-clone";
      case 12:
        return "fa-solid fa-people-roof";
      case 13:
        return "fa-solid fa-chart-bar";
      default:
        return "";
    }
  }
}
